export interface Point2D {
  x: number;
  y: number;
}

export interface PerspectivePoints {
  topLeft: Point2D;
  topRight: Point2D;
  bottomRight: Point2D;
  bottomLeft: Point2D;
}

export interface SurfaceBBox {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  width?: number;
  height?: number;
  x?: number;
  y?: number;
}

export type TargetSurface =
  | 'floor'
  | 'wall'
  | 'wardrobe'
  | 'base_cabinet'
  | 'wall_cabinet'
  | 'backsplash'
  | 'countertop'
  | 'ceiling'
  | 'furniture'
  | 'toilet'
  | 'meja'
  | 'laboratory';

export type ViewerMode = 'original' | 'preview' | 'result' | 'compare';

export interface GenerateSettings {
  targetClass: TargetSurface;
  conf: number;
  imgsz: number;
  opacity: number;
  rotationAngle: number;
  textureScale: number;
  textureOffsetX: number;
  textureOffsetY: number;
  brightness: number;
  contrast: number;
  minAreaRatio: number;
  keepLargest: boolean;
  floorTopLimitRatio: number;
  wallBottomLimitRatio: number;
  autoEstimateRoom: boolean;
  roomWidthM: number;
  roomDepthM: number;
  plankWidthM: number;
  plankLengthM: number;
  pixelsPerMeter: number;
  useRealScale: boolean;
  pattern: 'straight' | 'third' | 'random' | 'grid' | 'offset' | 'brick' | 'brick_1_3' | 'mix' | 'chessboard' | 'herringbone' | 'chevron' | 'basketweave' | 'tile';
  groutWidthM: number;
  groutEnabled: boolean;
  groutSizeMm: number;
  groutColor: string;
  perspectivePoints: PerspectivePoints | null;
}

export const DEFAULT_GENERATE_SETTINGS: GenerateSettings = {
  targetClass: 'floor',
  conf: 0.25,
  imgsz: 640,
  opacity: 0.78,
  rotationAngle: 0,
  textureScale: 1,
  textureOffsetX: 0,
  textureOffsetY: 0,
  brightness: 1.0,
  contrast: 1.0,
  minAreaRatio: 0.012,
  keepLargest: false,
  floorTopLimitRatio: 0.42,
  wallBottomLimitRatio: 0.86,
  autoEstimateRoom: true,
  roomWidthM: 4,
  roomDepthM: 5,
  plankWidthM: 0.1524,
  plankLengthM: 0.9144,
  pixelsPerMeter: 900,
  useRealScale: true,
  pattern: 'grid',
  groutWidthM: 0.005,
  groutEnabled: false,
  groutSizeMm: 5,
  groutColor: '#9E9E9E',
  perspectivePoints: null
};

export interface ProductTexture {
  id?: number | string;
  sku: string;
  code?: string;
  name: string;
  description: string;
  category: string;
  targetClass: TargetSurface;
  thumbnailUrl: string;
  textureUrl: string;
  plankWidthM?: number;
  plankLengthM?: number;
  defaultPattern?: GenerateSettings['pattern'] | string;
  textureRepeatScale?: number;
  defaultRotation?: number;
  defaultOffsetX?: number;
  defaultOffsetY?: number;
  defaultGroutSize?: number;
  groutColor?: string;
  availablePatterns?: Array<GenerateSettings['pattern'] | string>;
  groutSizes?: number[];
  coverage?: number;
  coverageType?: string;
  waste?: number;
  interior?: number | boolean;
  exterior?: number | boolean;
  width?: number;
  height?: number;
  sizeType?: string;
  type?: string;
  thickness?: string;
  category_image? :string;
}

export interface PreparedSurfaceInstance {
  id: string;
  surface: TargetSurface;
  maskUrl: string;
  sliceUrl: string;
  renderMaskUrl?: string | null;
  hardMaskUrl?: string | null;
  occlusionMaskUrl?: string | null;
  lightTextureUrl?: string | null;
  perspectiveQuad: PerspectivePoints | null;
  maskFilename?: string;
  sliceFilename?: string;
  nonzeroPixels?: number;
  confidence?: number;
  source?: string;
  bbox?: SurfaceBBox | null;
  centroid?: unknown;
}

export interface PreparedSurface {
  surface: TargetSurface;
  available: boolean;
  maskUrl: string;
  sliceUrl: string;
  renderMaskUrl?: string | null;
  hardMaskUrl?: string | null;
  occlusionMaskUrl?: string | null;
  lightTextureUrl?: string | null;
  perspectiveQuad: PerspectivePoints | null;
  maskFilename?: string;
  sliceFilename?: string;
  nonzeroPixels?: number;
  source?: string | null;
  reason?: string | null;
  quality?: string | null;
  isEstimated?: boolean;
  instances?: PreparedSurfaceInstance[];
}


export interface PreviewLayer {
  id: string;
  surface: TargetSurface;
  instanceId?: string;
  textureUrl: string;
  maskUrl: string | null;
  sliceUrl?: string | null;
  lightTextureUrl?: string | null;
  maskFlipY?: boolean;
  perspectiveQuad: PerspectivePoints | null;
  settings: GenerateSettings;
  opacity?: number;
  source?: string | null;
  quality?: string | null;
}


export interface SurfaceAssignment {
  surface: TargetSurface;
  instanceId?: string | null;
  product: ProductTexture;
  settings: GenerateSettings;
  textureUrl: string;
  customTextureFile?: File | null;
  aiLength?: number;
  aiWidth?: number;
  areaM2?: number;

  perspectiveQuad?: PerspectivePoints | null;

  // keep sementara biar kode lama nggak langsung error
  floorQuad?: PerspectivePoints | null;

  maskUrl?: string | null;
  sliceUrl?: string | null;
}

export interface SurfaceHotspot {
  surface: TargetSurface;
  instanceId?: string | null;
  label: string;
  hint: string;
  xRatio: number;
  yRatio: number;
  productSku: string | null;
  productName: string | null;
  productThumbnailUrl: string | null;
  isConfigured: boolean;
  confidence?: number;
  polygon?: Point2D[];
  estimatedSize?: EstimatedSurfaceSize | null;
}

export interface EstimatedSurfaceSize {
  unit?: string;
  width_mm?: number;
  height_mm?: number;
  length_mm?: number;
  depth_mm?: number;
  visible_width_mm?: number;
  visible_height_mm?: number;
  room_height_mm?: number;
  scale_mode?: string;
  confidence?: string;
  is_estimated?: boolean;
  estimation_source?: string;
}

export interface DetectSurfaceItem {
  id: string;
  class_id: number;
  class_name: TargetSurface | string;
  confidence: number;
  bbox: {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    width: number;
    height: number;
    center_x: number;
    center_y: number;
  };
  polygon?: Point2D[];
  label_point: Point2D;
}

export interface DetectSurfacesResponse {
  status: string;
  message?: string;
  target_class?: TargetSurface;
  image: {
    width: number;
    height: number;
    filename: string;
    url: string;
  };
  detections?: any;
  files?: {
    mask_filename?: string;
    slice_filename?: string;
    mask_url?: string;
    slice_url?: string;
    hard_mask_url?: string;
    occlusion_mask_url?: string;
    light_texture_url?: string;
  };
  room_assets?: any;
  hotspot?: PerspectivePoints | null;
  floor_quad?: PerspectivePoints | null;
  surface_quad?: PerspectivePoints | null;
  yolo_status?: any;
  debug?: any;
  error?: string;
}

export interface GenerateResponse {
  status: string;
  message?: string;
  error?: string;
  target_class?: TargetSurface | string;
  image_filename?: string;
  texture_filename?: string;
  mask_filename?: string;
  result_filename?: string;
  slice_filename?: string;
  image_path?: string;
  texture_path?: string;
  mask_path?: string;
  result_path?: string;
  slice_path?: string;
  mask_url?: string;
  result_url?: string;
  slice_url?: string;
  hard_mask_url?: string;
  occlusion_mask_url?: string;
  light_texture_url?: string;
  image_url?: string;
  texture_url?: string;
  room_estimation?: {
    room_width_m?: number;
    room_depth_m?: number;
    room_area_m2?: number;
    surface_area_m2?: number;
    confidence?: number;
    source?: string;
    [key: string]: unknown;
  } | null;
  surface_quad?: PerspectivePoints | null;
  perspective_quad?: PerspectivePoints | null;
  floor_quad?: PerspectivePoints | null;
  debug?: Record<string, unknown>;
  settings?: Record<string, unknown>;
  debug_urls?: Record<string, string>;
  render_meta?: Record<string, any>;
}

export interface VisualizerUrls {
  original: string | null;
  texture: string | null;
  result: string | null;
  mask: string | null;
  slice: string | null;
}

export interface RoomSample {
  id: string;
  title: string;
  group: string;
  imageUrl: string;
  processStatus?: string;
  processDone?: number | boolean;
  templateDir?: string | null;
  manifestUrl?: string | null;
  canVisualize?: number | boolean;
}
